import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Trash2, AlertTriangle, RefreshCw, Package, Truck, Info, Upload, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const GoodsTransferManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('bundle_barcode');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  
  // Entry list
  const [entries, setEntries] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [isLoadingEntries, setIsLoadingEntries] = useState(false);
  const [listSearchTerm, setListSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const limit = 20;
  
  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteSearchTerm, setDeleteSearchTerm] = useState('');
  const [deleteSearchType, setDeleteSearchType] = useState('bundle_barcode');
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // CSV bulk delete
  const [showCsvDialog, setShowCsvDialog] = useState(false);
  const [csvBundles, setCsvBundles] = useState([]);
  const [csvFileName, setCsvFileName] = useState('');
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const fetchEntries = async () => {
    setIsLoadingEntries(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', page * limit);
      params.append('limit', limit);
      if (listSearchTerm) params.append('search', listSearchTerm);
      
      const response = await apiClient.get(`/admin/goods-transfer-entries/list?${params}`);
      setEntries(response.data.entries || []);
      setTotalEntries(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Failed to load Goods Transfer entries');
    } finally {
      setIsLoadingEntries(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [page]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error('Please enter a search term');
      return;
    }
    
    setIsSearching(true);
    setSearchResults(null);
    try {
      const param = searchType === 'bundle_barcode' ? 'bundle_barcode' : 'entry_id';
      const response = await apiClient.get(`/admin/goods-transfer-entries/search?${param}=${encodeURIComponent(searchTerm.trim())}`);
      setSearchResults(response.data);
      
      if (!response.data.found) {
        toast.info('No Goods Transfer data found for this search term');
      }
    } catch (error) {
      console.error('Error searching:', error);
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  const openDeleteDialog = (term, type = 'bundle_barcode') => {
    setDeleteSearchTerm(term);
    setDeleteSearchType(type);
    setConfirmText('');
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    setIsDeleting(true);
    try {
      const param = deleteSearchType === 'bundle_barcode' ? 'bundle_barcode' : 'entry_id';
      const response = await apiClient.delete(
        `/admin/goods-transfer-entries?${param}=${encodeURIComponent(deleteSearchTerm)}`
      );
      
      toast.success(`Deleted ${response.data.total_records_deleted} records`);
      setShowDeleteDialog(false);
      setSearchResults(null);
      setSearchTerm('');
      fetchEntries();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleListSearch = () => {
    setPage(0);
    fetchEntries();
  };

  const handleCsvFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFileName(file.name);
    setBulkResult(null);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      // Parse: skip header if it looks like a header
      const codes = [];
      for (const line of lines) {
        const val = line.split(',')[0].trim().replace(/^["']|["']$/g, '');
        if (val && !val.toLowerCase().includes('bundle') && !val.toLowerCase().includes('code') && !val.toLowerCase().includes('barcode')) {
          codes.push(val);
        } else if (lines.indexOf(line) > 0) {
          codes.push(val);
        }
      }
      setCsvBundles(codes);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleBulkDelete = async () => {
    if (csvBundles.length === 0) return;
    setIsBulkDeleting(true);
    setBulkResult(null);
    try {
      const resp = await apiClient.post('/admin/goods-transfer-entries/bulk-delete', { bundle_codes: csvBundles });
      setBulkResult(resp.data);
      toast.success(resp.data.message);
      fetchEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="goods-transfer-management">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display flex items-center gap-2">
                <Truck className="w-6 h-6" />
                Goods Transfer Management
              </h1>
              <p className="text-sm text-[#E8DCC8]">Delete Goods Transfer entries and related data</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 space-y-6">
        {/* Search */}
        <Card className="border-[#E5DFD0]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#1A1F2E]">
              <Search className="w-5 h-5" />
              Search Entry
            </CardTitle>
            <CardDescription>
              Search by bundle barcode or entry ID to view and delete Goods Transfer data
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 flex-wrap">
              <select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                className="px-3 py-2 border rounded-md bg-white"
              >
                <option value="bundle_barcode">Bundle Barcode</option>
                <option value="entry_id">Entry ID</option>
              </select>
              <Input
                placeholder={searchType === 'bundle_barcode' ? 'Enter Bundle Barcode (e.g., BUN000001/25-26)' : 'Enter Entry ID'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1 min-w-[300px]"
                data-testid="search-input"
              />
              <Button 
                onClick={handleSearch} 
                disabled={isSearching}
                className="bg-cyan-600 hover:bg-cyan-700"
                data-testid="search-btn"
              >
                {isSearching ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                <span className="ml-2">Search</span>
              </Button>
            </div>

            {/* Search Results */}
            {searchResults && (
              <div className="mt-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-[#1A1F2E]">
                    Results for: {searchResults.search_term}
                  </h3>
                  {searchResults.found && (
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => openDeleteDialog(searchResults.search_term, searchResults.search_type)}
                      data-testid="delete-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete All Data
                    </Button>
                  )}
                </div>

                {searchResults.found ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Packing Entries */}
                    <Card className={searchResults.data.packing_entries ? 'border-cyan-300 bg-cyan-50' : 'border-gray-200 bg-gray-50'}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Package className="w-5 h-5 text-cyan-600" />
                          <h4 className="font-semibold">Packing Entries</h4>
                        </div>
                        {searchResults.data.packing_entries ? (
                          <p className="text-2xl font-bold text-cyan-600">{searchResults.data.packing_entries.count}</p>
                        ) : (
                          <p className="text-gray-500 text-sm">No data</p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Packed USNs */}
                    <Card className={searchResults.data.packed_usns ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Package className="w-5 h-5 text-green-600" />
                          <h4 className="font-semibold">Packed USNs</h4>
                        </div>
                        {searchResults.data.packed_usns ? (
                          <p className="text-2xl font-bold text-green-600">{searchResults.data.packed_usns.count}</p>
                        ) : (
                          <p className="text-gray-500 text-sm">No data</p>
                        )}
                      </CardContent>
                    </Card>

                    {/* FG Assignments */}
                    <Card className={searchResults.data.fg_assignments ? 'border-purple-300 bg-purple-50' : 'border-gray-200 bg-gray-50'}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Package className="w-5 h-5 text-purple-600" />
                          <h4 className="font-semibold">FG Assignments</h4>
                        </div>
                        {searchResults.data.fg_assignments ? (
                          <p className="text-2xl font-bold text-purple-600">{searchResults.data.fg_assignments.count}</p>
                        ) : (
                          <p className="text-gray-500 text-sm">No data</p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Bundles */}
                    <Card className={searchResults.data.bundles ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-gray-50'}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Package className="w-5 h-5 text-orange-600" />
                          <h4 className="font-semibold">Bundles</h4>
                        </div>
                        {searchResults.data.bundles ? (
                          <p className="text-2xl font-bold text-orange-600">{searchResults.data.bundles.count}</p>
                        ) : (
                          <p className="text-gray-500 text-sm">No data</p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Packing Records */}
                    <Card className={searchResults.data.packing_records ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-gray-50'}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Package className="w-5 h-5 text-blue-600" />
                          <h4 className="font-semibold">Packing Records</h4>
                        </div>
                        {searchResults.data.packing_records ? (
                          <p className="text-2xl font-bold text-blue-600">{searchResults.data.packing_records.count}</p>
                        ) : (
                          <p className="text-gray-500 text-sm">No data</p>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  <div className="p-4 bg-gray-100 rounded-lg text-center">
                    <Info className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-600">No Goods Transfer data found</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Entry List */}
        <Card className="border-[#E5DFD0]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-[#1A1F2E]">Recent Goods Transfer Entries</CardTitle>
                <CardDescription>Latest packing and assignment records</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <label className="cursor-pointer">
                  <Button variant="outline" size="sm" asChild>
                    <span>
                      <Upload className="w-4 h-4 mr-2" />
                      Import CSV to Delete
                    </span>
                  </Button>
                  <input type="file" accept=".csv,.txt" className="hidden" onChange={handleCsvFileChange} data-testid="csv-import-input" />
                </label>
                <Button variant="outline" size="sm" onClick={fetchEntries} disabled={isLoadingEntries}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingEntries ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3 mb-4">
              <Input
                placeholder="Search by entry ID, bundle barcode, or staff ID..."
                value={listSearchTerm}
                onChange={(e) => setListSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleListSearch()}
                className="flex-1"
              />
              <Button onClick={handleListSearch} variant="outline">
                <Search className="w-4 h-4" />
              </Button>
            </div>

            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entry ID</TableHead>
                    <TableHead>Bundle Barcode</TableHead>
                    <TableHead>Operation</TableHead>
                    <TableHead>Staff ID</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoadingEntries ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto text-cyan-600" />
                      </TableCell>
                    </TableRow>
                  ) : entries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        No entries found
                      </TableCell>
                    </TableRow>
                  ) : (
                    entries.map((entry, idx) => (
                      <TableRow key={`${entry.entry_id}-${idx}`}>
                        <TableCell className="font-mono text-sm">{entry.entry_id || '-'}</TableCell>
                        <TableCell className="font-mono text-sm">{entry.bundle_barcode || '-'}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            entry.operation === 'packing' ? 'bg-cyan-100 text-cyan-800' :
                            entry.operation === 'fg_assignment' ? 'bg-purple-100 text-purple-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {entry.operation || 'packing'}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">{entry.staff_id || '-'}</TableCell>
                        <TableCell className="text-sm text-gray-600">{formatDate(entry.created_at)}</TableCell>
                        <TableCell className="text-center">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => openDeleteDialog(entry.bundle_barcode || entry.entry_id, entry.bundle_barcode ? 'bundle_barcode' : 'entry_id')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {totalEntries > limit && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-gray-600">
                  Showing {page * limit + 1} - {Math.min((page + 1) * limit, totalEntries)} of {totalEntries}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
                    Previous
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={(page + 1) * limit >= totalEntries}>
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Delete Goods Transfer Data
            </DialogTitle>
            <DialogDescription>
              This will permanently delete all Goods Transfer data for:
              <span className="font-mono font-bold block mt-1">{deleteSearchTerm}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                <strong>This will delete:</strong>
              </p>
              <ul className="text-sm text-red-700 list-disc list-inside mt-1">
                <li>Packing entries</li>
                <li>Packed USN records</li>
                <li>FG assignments</li>
                <li>Bundle records</li>
              </ul>
            </div>

            <div>
              <Label className="text-red-700">Type DELETE to confirm:</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                className="mt-1 border-red-300 focus:border-red-500"
                data-testid="confirm-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting || confirmText !== 'DELETE'}
              data-testid="confirm-delete-btn"
            >
              {isDeleting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Bulk Delete Dialog */}
      <Dialog open={csvBundles.length > 0 && !bulkResult} onOpenChange={() => { setCsvBundles([]); setCsvFileName(''); setBulkResult(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-red-600" />
              Bulk Delete from CSV
            </DialogTitle>
            <DialogDescription>
              File: <strong>{csvFileName}</strong> — {csvBundles.length} bundle codes found
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-60 overflow-y-auto border rounded-lg p-3 space-y-1 bg-gray-50">
            {csvBundles.map((code, idx) => (
              <div key={idx} className="text-sm font-mono px-2 py-1 bg-white rounded border">{code}</div>
            ))}
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
            <AlertTriangle className="w-4 h-4 inline mr-1" />
            This will permanently delete all Goods Transfer data for these {csvBundles.length} bundles.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCsvBundles([]); setCsvFileName(''); }}>Cancel</Button>
            <Button variant="destructive" onClick={handleBulkDelete} disabled={isBulkDeleting} data-testid="confirm-bulk-delete-btn">
              {isBulkDeleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete {csvBundles.length} Bundles
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Result */}
      <Dialog open={!!bulkResult} onOpenChange={() => setBulkResult(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk Delete Complete</DialogTitle>
          </DialogHeader>
          {bulkResult && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Total</p>
                  <p className="text-xl font-bold">{bulkResult.total_bundles}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-xs text-green-600">Deleted</p>
                  <p className="text-xl font-bold text-green-700">{bulkResult.deleted}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3">
                  <p className="text-xs text-red-600">Failed</p>
                  <p className="text-xl font-bold text-red-700">{bulkResult.failed?.length || 0}</p>
                </div>
              </div>
              {bulkResult.failed?.length > 0 && (
                <div className="max-h-32 overflow-y-auto border rounded-lg p-2 bg-red-50 text-sm">
                  {bulkResult.failed.map((f, i) => (
                    <div key={i} className="text-red-700 font-mono text-xs">{f.code}: {f.reason}</div>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setBulkResult(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GoodsTransferManagement;
